import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Trash2, Plus, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_KEYS = new Set(['staff_pick', 'featured', 'editors_pick', 'new', 'rising', 'must_play', 'hidden_gem']);

export default function AdminTagsManager() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#06B6D4');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tags`);
      setTags(r.data?.tags || []);
    } catch { toast.error('Failed to load tags'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const key = newKey.trim().toLowerCase().replace(/\s+/g, '_');
    const label = newLabel.trim();
    if (!key || !label) { toast.error('Key and label required'); return; }
    try {
      await axios.post(`${API}/admin/tags`, { key, label, color: newColor }, { withCredentials: true });
      setNewKey(''); setNewLabel(''); setNewColor('#06B6D4');
      load();
      toast.success('Tag created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  const updateColor = async (t, color) => {
    try {
      await axios.patch(`${API}/admin/tags/${t.key}`, { color }, { withCredentials: true });
      setTags(prev => prev.map(x => x.key === t.key ? { ...x, color } : x));
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const updateLabel = async (t, label) => {
    if (!label || label === t.label) return;
    try {
      await axios.patch(`${API}/admin/tags/${t.key}`, { label }, { withCredentials: true });
      setTags(prev => prev.map(x => x.key === t.key ? { ...x, label } : x));
      toast.success('Label saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const remove = async (t) => {
    if (DEFAULT_KEYS.has(t.key)) { toast.error('Default tags cannot be deleted'); return; }
    if (!window.confirm(`Delete tag "${t.label}"?`)) return;
    try {
      await axios.delete(`${API}/admin/tags/${t.key}`, { withCredentials: true });
      setTags(prev => prev.filter(x => x.key !== t.key));
      toast.success('Deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5" data-testid="admin-tags-manager">
      <div className="flex items-center gap-2 mb-4">
        <Tag className="w-4 h-4 text-[#06B6D4]" />
        <h3 className="text-lg font-bold text-white">Tag Curation</h3>
        <span className="text-[10px] font-mono text-zinc-500 ml-auto">Colors show as thumbnail badges</span>
      </div>

      {/* Create new tag */}
      <div className="grid md:grid-cols-[140px_1fr_100px_auto] gap-2 mb-6 items-center">
        <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="key (e.g. rising_star)" className="bg-transparent border-[#1E1E24] text-xs" data-testid="tag-new-key" />
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Label (Rising Star)" className="bg-transparent border-[#1E1E24] text-xs" data-testid="tag-new-label" />
        <div className="flex items-center gap-1">
          <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-8 h-8 rounded border border-[#1E1E24] bg-transparent cursor-pointer" data-testid="tag-new-color" />
          <span className="text-[10px] font-mono text-zinc-500">{newColor}</span>
        </div>
        <Button onClick={create} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="tag-new-submit">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>

      {/* Existing */}
      {loading ? (
        <p className="text-xs text-zinc-500">Loading…</p>
      ) : (
        <div className="space-y-1.5">
          {tags.map(t => (
            <div key={t.key} className="flex items-center gap-2 bg-[#121214] border border-[#1E1E24] rounded-lg px-2 py-1.5" data-testid={`tag-row-${t.key}`}>
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase shadow"
                style={{ backgroundColor: t.color || '#06B6D4', color: '#000' }}
              >
                {t.label}
              </span>
              <input
                defaultValue={t.label}
                onBlur={(e) => updateLabel(t, e.target.value.trim())}
                className="flex-1 h-7 bg-transparent border border-transparent hover:border-[#1E1E24] focus:border-[#06B6D4] rounded px-2 text-xs text-white outline-none"
                data-testid={`tag-label-${t.key}`}
              />
              <input
                type="color"
                value={t.color || '#06B6D4'}
                onChange={(e) => updateColor(t, e.target.value)}
                className="w-7 h-7 rounded border border-[#1E1E24] bg-transparent cursor-pointer"
                title="Change color"
                data-testid={`tag-color-${t.key}`}
              />
              <span className="text-[10px] font-mono text-zinc-500 min-w-[70px]">{t.key}</span>
              {DEFAULT_KEYS.has(t.key) ? (
                <span className="text-[9px] font-mono text-zinc-600 uppercase">default</span>
              ) : (
                <button onClick={() => remove(t)} className="text-zinc-500 hover:text-red-400" data-testid={`tag-delete-${t.key}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
